import * as React from "react";
import { TextInputEditor } from "../internal/text-input-editor";

export type DateValue = string | null;

/**
 * Convert an ISO-ish input into the YYYY-MM-DD form expected by
 * `<input type="date">`. Uses UTC date parts so a midnight-UTC string
 * doesn't shift to the previous day.
 */
function toDateInputValue(value: DateValue): string {
  if (!value) return "";
  const str = String(value);
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) {
    // Fall back to a literal YYYY-MM-DD prefix if present.
    return /^\d{4}-\d{2}-\d{2}/.test(str) ? str.slice(0, 10) : "";
  }
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function DateEditor({
  value,
  onCommit,
  onCancel,
}: {
  value: DateValue;
  onCommit: (next: DateValue) => void;
  onCancel: () => void;
}) {
  const parse = React.useCallback(
    (draft: string) => ({ ok: true as const, value: draft === "" ? null : draft }),
    [],
  );
  return (
    <TextInputEditor<DateValue>
      initial={toDateInputValue(value)}
      parse={parse}
      onCommit={onCommit}
      onCancel={onCancel}
      inputType="date"
    />
  );
}
